// Adapted from pingdotgg/t3code's QueuedRunsControl (MIT), 7202ba666.
// Upstream's queue lives on the server (runs, reorder/promote/cancel commands);
// Amoreg's is the client-side queuedMessageStore, so this control only renders
// the store's entries and reports intents. Sending, steering and removal stay
// in ChatView, which owns the send path and the composer draft.
import {
  ArrowUpIcon,
  ChevronDownIcon,
  CornerUpRightIcon,
  GripVerticalIcon,
  ListOrderedIcon,
  PaperclipIcon,
  PauseIcon,
  PencilIcon,
  XIcon,
} from "lucide-react";
import { useId, useLayoutEffect, useRef, useState } from "react";

import { stripInlineTerminalContextPlaceholders } from "~/lib/terminalContext";
import { cn } from "~/lib/utils";
import { queuedMessageDropTarget, type QueuedComposerMessage } from "../../queuedMessageStore";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

const QUEUED_MESSAGE_DRAG_TYPE = "application/x-amoreg-queued-message";
const QUEUE_VISIBLE_ROWS = 3;

/** One line of preview text; a message of attachments alone says so. */
export function queuedMessagePreviewText(message: QueuedComposerMessage): string {
  const text = stripInlineTerminalContextPlaceholders(message.prompt).replace(/\s+/g, " ").trim();
  if (text) return text;
  const attachmentCount = message.context.images.length + message.context.files.length;
  if (attachmentCount > 0) {
    return attachmentCount === 1 ? "1 attachment" : `${attachmentCount} attachments`;
  }
  return "Attached context";
}

export interface QueuedMessagesControlProps {
  readonly messages: ReadonlyArray<QueuedComposerMessage>;
  /** A running reply turns Send into Steer: the message joins the reply in progress. */
  readonly running: boolean;
  /** Approvals, questions, reconnects and in-flight sends hold every manual send. */
  readonly sendDisabled: boolean;
  readonly onSend: (message: QueuedComposerMessage) => void;
  readonly onEdit: (message: QueuedComposerMessage) => void;
  readonly onRemove: (message: QueuedComposerMessage) => void;
  readonly onMove: (id: string, beforeId: string | null) => void;
}

/** Above a row when the pointer is in its upper half, below it otherwise. */
function rowInsertIndex(
  event: { readonly clientY: number; readonly currentTarget: Element },
  index: number,
): number {
  const rect = event.currentTarget.getBoundingClientRect();
  return event.clientY < rect.top + rect.height / 2 ? index : index + 1;
}

export function QueuedMessagesControl({
  messages,
  running,
  sendDisabled,
  onSend,
  onEdit,
  onRemove,
  onMove,
}: QueuedMessagesControlProps) {
  const [showAll, setShowAll] = useState(false);
  const queueListId = useId();
  // Live drag-reorder state: the dragged message and the queue index the row
  // would be inserted at (0..messages.length) as of the latest dragover.
  const [dragState, setDragState] = useState<{
    readonly id: string;
    readonly insertIndex: number | null;
  } | null>(null);
  // Drags must start from the grip, not from an accidental text-drag on the
  // row. The grip arms its message id on pointerdown; dragstart verifies it.
  const dragArmedIdRef = useRef<string | null>(null);
  // A keyed row that moves is re-inserted into the DOM, which drops focus;
  // keyboard reordering hands it back to the moved row's grip.
  const refocusGripIdRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const id = refocusGripIdRef.current;
    if (id === null) return;
    refocusGripIdRef.current = null;
    const list = document.getElementById(queueListId);
    for (const grip of list?.querySelectorAll<HTMLElement>("[data-queued-grip]") ?? []) {
      if (grip.dataset.queuedGrip === id) grip.focus();
    }
  }, [messages, queueListId]);

  if (messages.length === 0) return null;

  const ids = messages.map((message) => message.id);
  const canReorder = messages.length > 1;
  const paused = messages.some((message) => message.held);

  const completeDrag = (id: string, insertIndex: number | null) => {
    setDragState(null);
    dragArmedIdRef.current = null;
    if (insertIndex === null) return;
    const beforeId = queuedMessageDropTarget(ids, id, insertIndex);
    if (beforeId !== undefined) onMove(id, beforeId);
  };

  const moveByKeyboard = (id: string, beforeId: string | null) => {
    refocusGripIdRef.current = id;
    onMove(id, beforeId);
  };

  const collapsible = messages.length > QUEUE_VISIBLE_ROWS;
  const visibleMessages =
    collapsible && !showAll ? messages.slice(0, QUEUE_VISIBLE_ROWS - 1) : messages;
  const countLabel = `${messages.length} queued message${messages.length === 1 ? "" : "s"}`;

  return (
    <section
      aria-label={`Up next: ${countLabel}`}
      data-queued-messages="true"
      className="flex w-full min-w-0 flex-col gap-2 px-1 pb-4"
    >
      <h3 className="flex min-h-5 items-center gap-1.5 text-xs font-normal text-muted-foreground [&_svg]:size-3.5 [&_svg]:shrink-0">
        <ListOrderedIcon aria-hidden />
        Up next
        <span className="tabular-nums" aria-live="polite">
          {messages.length} queued
        </span>
        {paused ? <span className="text-muted-foreground/70">Paused</span> : null}
      </h3>
      <ol className="flex flex-col items-end gap-3" id={queueListId}>
        {visibleMessages.map((message, index) => {
          const fullText = stripInlineTerminalContextPlaceholders(message.prompt).trim();
          const images = message.context.images;
          const fileCount = message.context.files.length;
          return (
            <li
              key={message.id}
              data-queued-message-held={message.held ? "true" : undefined}
              className={cn(
                "group/queued relative flex w-full flex-col items-end gap-1",
                dragState !== null && dragState.id === message.id && "opacity-50",
              )}
              draggable={canReorder}
              onDragStart={(event) => {
                if (dragArmedIdRef.current !== message.id) {
                  event.preventDefault();
                  return;
                }
                event.dataTransfer.setData(QUEUED_MESSAGE_DRAG_TYPE, message.id);
                event.dataTransfer.effectAllowed = "move";
                setDragState({ id: message.id, insertIndex: null });
              }}
              onDragEnd={() => {
                dragArmedIdRef.current = null;
                setDragState(null);
              }}
              onDragOver={(event) => {
                if (dragState === null) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                const insertIndex = rowInsertIndex(event, index);
                if (dragState.insertIndex !== insertIndex) {
                  setDragState({ id: dragState.id, insertIndex });
                }
              }}
              onDrop={(event) => {
                if (dragState === null) return;
                event.preventDefault();
                // The drop's own position decides: a browser need not send a
                // dragover right before the drop, so the last indicator
                // position can be stale.
                completeDrag(dragState.id, rowInsertIndex(event, index));
              }}
            >
              <span className="sr-only">
                {`Queued message ${index + 1} of ${messages.length}, not sent: `}
              </span>
              {dragState?.insertIndex === index ? (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 -top-1.5 h-0.5 rounded bg-primary/70"
                />
              ) : null}
              {index === visibleMessages.length - 1 &&
              dragState?.insertIndex === visibleMessages.length ? (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 -bottom-1.5 h-0.5 rounded bg-primary/70"
                />
              ) : null}
              <div className="flex min-w-0 max-w-[80%] flex-col gap-2 rounded-2xl border border-dashed border-muted-foreground/40 px-3 py-2 text-sm text-muted-foreground">
                {images.length > 0 || fileCount > 0 ? (
                  <span className="flex flex-wrap items-center gap-1">
                    {images.map((image) => (
                      <span
                        key={image.id}
                        className="size-8 overflow-hidden rounded-md border border-border/70 bg-background"
                      >
                        <img
                          src={image.previewUrl}
                          alt={image.name}
                          className="size-full object-cover"
                        />
                      </span>
                    ))}
                    {fileCount > 0 ? (
                      <span
                        aria-label={fileCount === 1 ? "1 file" : `${fileCount} files`}
                        className="flex items-center gap-0.5 text-xs tabular-nums"
                      >
                        <PaperclipIcon aria-hidden className="size-3" />
                        {fileCount > 1 ? fileCount : null}
                      </span>
                    ) : null}
                  </span>
                ) : null}
                <p className="whitespace-pre-wrap wrap-anywhere">
                  {fullText || queuedMessagePreviewText(message)}
                </p>
              </div>
              <div className="flex items-center gap-0.5 pe-1 text-xs text-muted-foreground">
                {canReorder ? (
                  <Button
                    size="icon-xs"
                    variant="ghost-muted"
                    aria-label="Reorder queued message (drag, or press the arrow keys)"
                    data-queued-grip={message.id}
                    className="group/grip cursor-grab active:cursor-grabbing"
                    onPointerDown={() => {
                      dragArmedIdRef.current = message.id;
                    }}
                    // A click that never became a drag must not leave the row
                    // armed for a drag started elsewhere on it. A real drag
                    // has already passed dragstart by the time the browser
                    // cancels the pointer.
                    onPointerUp={() => {
                      dragArmedIdRef.current = null;
                    }}
                    onPointerCancel={() => {
                      dragArmedIdRef.current = null;
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "ArrowUp" && index > 0) {
                        event.preventDefault();
                        moveByKeyboard(message.id, ids[index - 1] ?? null);
                      }
                      if (event.key === "ArrowDown" && index < messages.length - 1) {
                        event.preventDefault();
                        // Moving into the hidden rows would unmount the focused grip.
                        if (index + 1 >= visibleMessages.length) setShowAll(true);
                        moveByKeyboard(message.id, ids[index + 2] ?? null);
                      }
                    }}
                  >
                    <span className="text-xs font-medium tabular-nums group-hover/queued:hidden group-focus-visible/grip:hidden">
                      {index + 1}
                    </span>
                    <GripVerticalIcon className="hidden group-hover/queued:block group-focus-visible/grip:block" />
                  </Button>
                ) : null}
                {message.held ? (
                  <PauseIcon aria-label="Paused" className="mx-1 size-3 text-muted-foreground/60" />
                ) : null}
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        size="icon-xs"
                        variant="ghost-muted"
                        aria-label="Edit queued message"
                        onClick={() => onEdit(message)}
                      />
                    }
                  >
                    <PencilIcon />
                  </TooltipTrigger>
                  <TooltipPopup>Edit in the composer</TooltipPopup>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger render={<span className="flex shrink-0" />}>
                    <Button
                      size="xs"
                      variant="ghost-muted"
                      disabled={sendDisabled}
                      onClick={() => onSend(message)}
                    >
                      {running ? <CornerUpRightIcon /> : <ArrowUpIcon />}
                      {running ? "Steer" : "Send"}
                    </Button>
                  </TooltipTrigger>
                  <TooltipPopup>
                    {sendDisabled
                      ? "Available once the current action finishes"
                      : running
                        ? "Send now, into the reply in progress"
                        : "Send now"}
                  </TooltipPopup>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        size="icon-xs"
                        variant="ghost-muted"
                        aria-label="Remove queued message"
                        onClick={() => onRemove(message)}
                      />
                    }
                  >
                    <XIcon />
                  </TooltipTrigger>
                  <TooltipPopup>Remove from queue</TooltipPopup>
                </Tooltip>
              </div>
            </li>
          );
        })}
      </ol>
      {collapsible ? (
        <Button
          size="xs"
          variant="ghost-muted"
          className="self-end"
          aria-expanded={showAll}
          aria-controls={queueListId}
          // Keep the composer focused; keyboard activation still works.
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => setShowAll((value) => !value)}
        >
          {showAll ? "Show fewer" : `Show all ${messages.length} queued`}
          <ChevronDownIcon
            aria-hidden
            className={cn("transition-transform duration-150", showAll && "rotate-180")}
          />
        </Button>
      ) : null}
    </section>
  );
}
